import React, { useState } from "react";
import "@/App.css";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Zap,
  BadgeCheck,
  FileCheck2,
  Plane,
  Globe2,
  Warehouse,
  Hotel,
  ShoppingCart,
  Factory,
  Wrench,
  Truck,
  BedDouble,
  Utensils,
  Bus,
  HeartPulse,
  ClipboardCheck,
  Menu,
  X,
} from "lucide-react";

/* ---------------- Shared constants (business card) ---------------- */
const PHONE = "+917989877077";
const PHONE_DISPLAY = "+91 79898 77077";
const EMAIL = "info@europathimmigration.com";
const WEBSITE = "https://www.europathimmigration.com";
const WHATSAPP_URL = `https://wa.me/917989877077?text=${encodeURIComponent(
  "Hello EuroPath Immigration Services, I am interested in European job opportunities. Please share details."
)}`;
/** Visiting / postal address */
const OFFICE_ADDRESS_ONE_LINE =
  "Plot No 8, Venkateshwar Colony, Pillar No C867, Near Uppal Metro Station, Uppal, Hyderabad – 500039";
const BASE = process.env.PUBLIC_URL || "";
const asset = (p) => `${BASE}${p.startsWith("/") ? p : `/${p}`}`;
const LOGO = asset("/euro-path-logo.png");
const HERO_IMG =
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=2000&q=80";

/** Google Maps embed (Share → Embed) — EuroPath Immigration Services */
const GOOGLE_MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.271567377973!2d78.5614457!3d17.3987503!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99907786b4c7%3A0x9e30bfe04bb8d9fd!2sEuroPath%20Immigration%20Services!5e0!3m2!1sen!2sin!4v1780752830969!5m2!1sen!2sin";

/** Same place as embed — open full map in a new tab */
const GOOGLE_MAPS_PLACE_URL =
  "https://www.google.com/maps/place/EuroPath+Immigration+Services/@17.3987503,78.5614457,17z";

const COUNTRIES = [
  {
    name: "Netherlands",
    code: "nl",
    tag: "€2000 – €3000 / month",
    blurb:
      "Warehouse, logistics, horticulture and factory placements with accommodation & transport.",
    img:
      "https://images.unsplash.com/photo-1620495366039-059b09f79261?auto=format&fit=crop&w=1400&q=80",
  },
  {
    name: "France",
    code: "fr",
    tag: "€2000 – €3000 / month",
    blurb:
      "Hotel staff, cooking assistants and skilled trades with 2-year work visa support.",
    img:
      "https://images.unsplash.com/photo-1609885728169-8737a953a103?auto=format&fit=crop&w=1400&q=80",
  },
  {
    name: "Latvia",
    code: "lv",
    tag: "€2000 – €3000 / month",
    blurb:
      "Construction workers, warehouse staff and logistics roles with complete relocation support.",
    img:
      "https://images.unsplash.com/photo-1567942712661-82b9b407abbf?auto=format&fit=crop&w=1400&q=80",
  },
  {
    name: "Finland",
    code: "fi",
    tag: "€2000 – €3000 / month",
    blurb:
      "Warehouse, logistics, hospitality and manufacturing roles with strong worker protections and clear contracts.",
    img:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1400&q=80",
  },
];

const SECTORS = [
  {
    icon: Warehouse,
    name: "Warehouses",
    roles: ["Packing", "Loading & Unloading"],
  },
  {
    icon: Hotel,
    name: "Hotels",
    roles: ["Waiter", "Cooking Assistant", "Cleaning Staff"],
  },
  {
    icon: ShoppingCart,
    name: "Supermarkets",
    roles: ["Cashier", "Salesperson", "Supervisor"],
  },
  {
    icon: Factory,
    name: "Factory Jobs",
    roles: ["Packing", "Helpers", "Machine Operators"],
  },
  {
    icon: Wrench,
    name: "Skilled Jobs",
    roles: ["Electricians", "Plumbers", "Welders"],
  },
  {
    icon: Truck,
    name: "Drivers & More",
    roles: ["Drivers & Cleaners", "Construction Workers"],
  },
];

const BENEFITS = [
  { icon: BedDouble, label: "Free Accommodation" },
  { icon: Utensils, label: "Food Provided" },
  { icon: Bus, label: "Transport Support" },
  { icon: HeartPulse, label: "Medical Cover" },
  { icon: FileCheck2, label: "2-Year Work Visa" },
  { icon: ClipboardCheck, label: "Overtime Available" },
];

const WHY_US = [
  {
    icon: ShieldCheck,
    title: "Trusted Immigration Partner",
    text:
      "Transparent processing, genuine documentation and a single point of contact from start to landing.",
  },
  {
    icon: Zap,
    title: "Fast & Organised Processing",
    text:
      "Structured timelines for visa, interview and deployment — no endless waits or hidden delays.",
  },
  {
    icon: BadgeCheck,
    title: "Genuine Job Offers",
    text:
      "Verified European employers with contracts, salary commitments and worker protections.",
  },
  {
    icon: Plane,
    title: "End-to-End Visa Support",
    text:
      "From documentation and embassy filing to flight and arrival assistance — we handle it all.",
  },
];

const JOB_MARQUEE = [
  "Auto Denter", "Auto Electrician", "Auto Mechanic", "Baker", "Bartender",
  "Beautician", "Butcher", "Caregiver", "Carpenter", "Cook", "Diesel Mechanic",
  "Electrician", "Engineer", "Excavator Operator", "Fitter", "Heavy Driver",
  "JCB Operator", "CNC Operator", "Mason", "Nanny", "Painter", "Pastry Chef",
  "Plumber", "Professional Driver", "Tailor", "Team Leader", "Welder",
];

/* ---------------- Brand Logo ---------------- */
const BrandLogo = ({ className = "" }) => (
  <img
    src={LOGO}
    alt="EuroPath Immigration Services"
    className={`h-11 md:h-14 w-auto max-h-full object-contain object-left border-0 outline-none ring-0 shadow-none block ${className}`}
  />
);

/* ---------------- Header ---------------- */
const Header = () => {
  const [open, setOpen] = useState(false);

  const navLinks = [
    { href: "#countries", label: "Countries" },
    { href: "#sectors", label: "Job Sectors" },
    { href: "#benefits", label: "Salary & Benefits" },
    { href: "#why", label: "Why Us" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 fe-theme-header shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <a
            href="#top"
            data-testid="brand-link"
            className="group inline-flex items-center border-0 outline-none ring-0 shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f7f3e8]"
          >
            <BrandLogo />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[13.5px] font-medium text-zinc-800 hover:text-black transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${PHONE}`}
              data-testid="header-call-btn"
              className="hidden md:inline-flex items-center gap-2 px-4 h-10 border border-zinc-400 text-zinc-900 text-[13px] font-semibold hover:border-zinc-700 hover:text-black transition-colors"
            >
              <Phone className="w-4 h-4" /> Call
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 md:px-5 h-10 bg-white hover:bg-zinc-200 text-black text-[13px] font-bold transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
              <span className="sm:hidden">Chat</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-zinc-400 text-zinc-900"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-nav"
            className="lg:hidden border-t border-[#ddd4c0] py-4 grid gap-1"
          >
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-2 py-3 text-[15px] font-medium text-zinc-900 hover:bg-black/5"
              >
                {l.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[88vh] flex items-center overflow-hidden pt-20 md:pt-24 fe-theme-hero"
  >
    <div className="absolute inset-0 pointer-events-none">
      <img
        src={HERO_IMG}
        alt=""
        aria-hidden
        className="w-full h-full object-cover fe-theme-hero-media"
      />
      <div className="absolute inset-0 fe-theme-hero-overlay" />
      <div className="absolute inset-0 fe-grid-bg opacity-50" />
    </div>

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 text-white fe-rise">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-500/60 bg-zinc-700/50 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full" />
            <span className="fe-overline text-zinc-400">
              Europe Job Placements · 2026 Intake
            </span>
          </div>

          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.04] text-white">
            Your Gateway
            <br />
            to a{" "}
            <span className="relative inline-block">
              <span className="text-zinc-300 italic">Brighter Future</span>
              <svg
                className="absolute -bottom-2 left-0 w-full text-zinc-400"
                viewBox="0 0 300 12"
                fill="none"
              >
                <path
                  d="M2 9 C 80 2, 160 2, 298 8"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="block mt-2 text-zinc-100">in Europe.</span>
          </h1>

          <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">
            EuroPath Immigration Services places skilled and unskilled workers
            in <span className="text-zinc-200 font-semibold">Netherlands, France, Latvia & Finland</span>{" "}
            — with genuine work visas, salaries of{" "}
            <span className="font-semibold text-white">€2,000 – €3,000/month</span>{" "}
            and complete accommodation, food, transport & medical support.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-2.5 px-6 h-14 bg-white hover:bg-zinc-200 text-black text-[15px] font-bold transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={`tel:${PHONE}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2.5 px-6 h-14 border border-zinc-400 text-white hover:bg-zinc-600/80 text-[15px] font-semibold transition-colors"
            >
              <Phone className="w-5 h-5" />
              {PHONE_DISPLAY}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
            <Stat value="4" label="European Countries" />
            <Stat value="40+" label="Job Roles" />
            <Stat value="2yr" label="Work Visa" />
          </div>
        </div>

        <div className="lg:col-span-4 hidden lg:block">
          <div className="relative fe-rise" style={{ animationDelay: "0.2s" }}>
            <div className="absolute -inset-4 border border-zinc-600" />
            <div className="relative fe-card p-6 shadow-2xl">
              <div className="flex items-start justify-between">
                <span className="fe-overline text-zinc-400">
                  Current Opening
                </span>
                <span className="fi fi-nl text-2xl" />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mt-3">
                Netherlands — Limited Seats
              </h3>
              <p className="text-sm text-zinc-400 mt-2">
                Machine Operators · Helpers · Packing Staff · Drivers
              </p>
              <div className="mt-4 p-4 fe-panel-inset border">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                  Salary Package
                </div>
                <div className="font-display text-3xl font-bold text-zinc-100 mt-1">
                  €2000 – €3000
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Per month + overtime
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-zinc-300">
                {["Free stay", "Food", "Transport", "Medical"].map((b) => (
                  <div key={b} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" />
                    {b}
                  </div>
                ))}
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-apply-card-btn"
                className="mt-5 inline-flex items-center justify-center w-full h-11 bg-white hover:bg-zinc-200 text-black text-sm font-semibold transition-colors"
              >
                Apply Now →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <a
      href="#about"
      className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 text-xs tracking-[0.3em] uppercase hover:text-white transition-colors"
      data-testid="hero-scroll-indicator"
    >
      Scroll ↓
    </a>
  </section>
);

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display text-3xl md:text-4xl font-bold text-zinc-300">
      {value}
    </div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1 font-semibold">
      {label}
    </div>
  </div>
);

/* ---------------- Marquee strip ---------------- */
const JobMarquee = () => (
  <div
    data-testid="job-marquee"
    className="fe-theme-band py-4 overflow-hidden border-y"
  >
    <div className="flex fe-marquee-track whitespace-nowrap">
      {[...JOB_MARQUEE, ...JOB_MARQUEE].map((job, i) => (
        <span
          key={i}
          className="flex items-center gap-3 px-6 text-sm text-zinc-600"
        >
          <span className="w-1 h-1 bg-zinc-500 rounded-full" />
          <span className="font-medium">{job}</span>
        </span>
      ))}
    </div>
  </div>
);

/* ---------------- About / Trust Strip ---------------- */
const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="py-20 md:py-28 fe-section-dark fe-stamp-bg text-zinc-300"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <span className="fe-divider-gold" />
          <div className="fe-overline text-zinc-400 mb-4">
            About EuroPath · Est. Hyderabad
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
            A trusted bridge between{" "}
            <span className="italic text-zinc-400">Indian talent</span> and
            European employers.
          </h2>
          <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
            EuroPath Immigration Services specialises in work permits, visit
            visas and study visas across Europe. We are a preferred partner
            for candidates seeking genuine, contract-backed European
            placements with complete relocation support — from documentation
            and embassy filing to flight and arrival assistance.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-8">
            <TrustStat number="4" label="Countries Served" />
            <TrustStat number="100%" label="Genuine Contracts" />
            <TrustStat number="2 Yrs" label="Work Visa Duration" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -top-5 -left-5 w-24 h-24 border-2 border-zinc-300" />
            <div className="absolute -bottom-5 -right-5 w-24 h-24 border-2 border-zinc-500" />
            <div className="relative fe-panel-inset border p-8">
              <div className="fe-overline text-zinc-400">
                Hyderabad Head Office
              </div>
              <h3 className="font-display text-2xl md:text-[28px] font-bold text-white mt-2 leading-tight">
                Visit us · Schedule a meeting
              </h3>
              <div className="h-px bg-zinc-700 my-5" />
              <ul className="space-y-3.5 text-sm text-zinc-300">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-0.5 text-zinc-600 flex-shrink-0" />
                  <a
                    href={`tel:${PHONE}`}
                    className="hover:text-zinc-600 font-medium"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 text-zinc-600 flex-shrink-0" />
                  <a
                    href={`mailto:${EMAIL}`}
                    className="hover:text-zinc-600 break-all font-medium"
                  >
                    {EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-zinc-600 flex-shrink-0" />
                  <span>
                    Plot No 8, Venkateshwar Colony, Pillar No C867,
                    <br />
                    Near Uppal Metro Station, Uppal,
                    <br />
                    Hyderabad – 500039
                  </span>
                </li>
              </ul>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="about-whatsapp-btn"
                className="mt-6 inline-flex items-center justify-center w-full h-12 bg-white hover:bg-zinc-200 text-black text-sm font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Book a Meeting on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TrustStat = ({ number, label }) => (
  <div className="border-l-2 border-zinc-300 pl-4">
    <div className="font-display text-4xl font-bold text-zinc-600">
      {number}
    </div>
    <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mt-1">
      {label}
    </div>
  </div>
);

/* ---------------- Countries ---------------- */
const Countries = () => (
  <section
    id="countries"
    data-testid="countries-section"
    className="py-20 md:py-28 fe-section-dark fe-grid-bg text-zinc-300"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="fe-divider-gold" />
        <div className="fe-overline text-zinc-400 mb-4">
          Destinations · Europe
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
          Countries we currently{" "}
          <span className="italic text-zinc-400">place candidates in.</span>
        </h2>
        <p className="mt-5 text-zinc-400 text-base md:text-lg">
          Verified employers, legal work contracts and complete relocation
          support across four European nations.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {COUNTRIES.map((c, i) => (
          <article
            key={c.code}
            data-testid={`country-card-${c.code}`}
            className="group relative fe-surface-dark border border-zinc-600/40 overflow-hidden hover:border-zinc-400/60 transition-colors fe-rise"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <div className="relative h-60 overflow-hidden">
              <img
                src={c.img}
                alt={c.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-600/70 via-zinc-600/10 to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-zinc-700/90 backdrop-blur-sm border border-zinc-500/50">
                <span className={`fi fi-${c.code} text-lg`} />
                <span className="text-xs font-bold text-zinc-100 tracking-wide">
                  {c.name.toUpperCase()}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 text-white">
                <div className="text-xs tracking-[0.18em] uppercase font-semibold opacity-90">
                  {c.tag}
                </div>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
                {c.name}
              </h3>
              <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed">
                {c.blurb}
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`country-cta-${c.code}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-zinc-500 transition-colors"
              >
                Enquire about {c.name}
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Job Sectors ---------------- */
const Sectors = () => (
  <section
    id="sectors"
    data-testid="sectors-section"
    className="py-20 md:py-28 fe-section-dark text-zinc-300"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
        <div className="lg:col-span-7">
          <span className="fe-divider-gold" />
          <div className="fe-overline text-zinc-400 mb-4">
            Available Job Sectors
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
            From <span className="italic text-zinc-400">unskilled</span> to{" "}
            <span className="italic text-zinc-400">highly-skilled</span> — we
            place talent across every shift.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-zinc-400 text-base md:text-lg">
            Warehouses, hotels, supermarkets, factories, skilled trades and
            transport — six core sectors covering 40+ verified roles across
            Europe.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-600/50">
        {SECTORS.map((s, i) => (
          <div
            key={s.name}
            data-testid={`sector-${s.name.toLowerCase().replace(/\s&/g, "").replace(/\s/g, "-")}`}
            className={`group relative p-7 md:p-8 fe-surface-dark fe-surface-dark-hover transition-colors duration-300 border-zinc-600/40 ${
              i % 3 !== 2 ? "lg:border-r" : ""
            } ${i < SECTORS.length - (SECTORS.length % 3 || 3) ? "lg:border-b" : ""} ${
              i % 2 === 0 ? "md:border-r lg:border-r" : ""
            } ${i < SECTORS.length - 2 ? "border-b md:border-b" : "border-b md:border-b-0"}`}
          >
            <div className="w-12 h-12 flex items-center justify-center border border-zinc-600/40 group-hover:border-zinc-400 group-hover:bg-zinc-400 transition-colors">
              <s.icon className="w-5 h-5 text-zinc-600 group-hover:text-zinc-200 transition-colors" />
            </div>
            <div className="fe-overline text-zinc-500 group-hover:text-zinc-400 mt-6 transition-colors">
              0{i + 1} / 06
            </div>
            <h3 className="font-display text-2xl font-bold text-white group-hover:text-white mt-2 transition-colors">
              {s.name}
            </h3>
            <ul className="mt-4 space-y-1.5">
              {s.roles.map((r) => (
                <li
                  key={r}
                  className="flex items-center gap-2 text-sm text-zinc-400 group-hover:text-zinc-200 transition-colors"
                >
                  <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-10 p-6 md:p-8 fe-panel-inset border flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div>
          <div className="fe-overline text-zinc-400">40+ Roles</div>
          <h3 className="font-display text-xl md:text-2xl font-bold text-white mt-1">
            Don't see your trade? We have many more skilled & unskilled roles.
          </h3>
        </div>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="sectors-enquire-btn"
          className="inline-flex items-center gap-2 px-6 h-12 bg-white hover:bg-zinc-200 text-black text-sm font-semibold transition-colors self-start md:self-auto"
        >
          <MessageCircle className="w-4 h-4" />
          Ask about your role
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- Salary & Benefits ---------------- */
const Benefits = () => (
  <section
    id="benefits"
    data-testid="benefits-section"
    className="relative py-20 md:py-28 fe-section-dark overflow-hidden fe-grid-bg text-zinc-300"
  >
    <div className="relative max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="fe-divider-gold" />
          <div className="fe-overline text-zinc-400 mb-4">
            Salary & Benefits
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
            Earn in{" "}
            <span className="italic text-zinc-400">Euros.</span>
            <br />
            Live with every{" "}
            <span className="italic text-zinc-400">comfort.</span>
          </h2>

          <div className="mt-10 space-y-5">
            <div className="p-6 fe-surface-dark border border-zinc-700/80 shadow-sm">
              <div className="fe-overline text-zinc-400">Monthly Salary</div>
              <div className="font-display text-4xl md:text-5xl font-bold text-white mt-1">
                €2000 – €3000
              </div>
              <div className="text-sm text-zinc-500 mt-1">
                Approx. ₹1,80,000 – ₹2,70,000 per month
              </div>
              <div className="h-px bg-zinc-600/60 my-4" />
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
                <span className="font-semibold">Overtime = Extra Income</span>
              </div>
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="benefits-apply-btn"
              className="inline-flex items-center gap-2 px-6 h-13 py-4 bg-white hover:bg-zinc-200 text-black font-bold text-[15px] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Apply via WhatsApp
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-0 border border-zinc-700/80 fe-surface-dark">
            {BENEFITS.map((b, i) => (
              <div
                key={b.label}
                data-testid={`benefit-${b.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`p-6 md:p-7 flex items-start gap-4 ${
                  i % 2 === 0 ? "sm:border-r border-zinc-300" : ""
                } ${i < BENEFITS.length - 2 ? "border-b border-zinc-300" : "border-b sm:border-b-0"} ${
                  i < BENEFITS.length - (BENEFITS.length % 2 || 2) ? "sm:border-b border-zinc-300" : ""
                }`}
              >
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-zinc-300">
                  <b.icon className="w-5 h-5 text-zinc-100" />
                </div>
                <div>
                  <div className="fe-overline text-zinc-500">
                    Benefit 0{i + 1}
                  </div>
                  <div className="font-display text-xl md:text-2xl font-bold text-white mt-1">
                    {b.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-5 border border-zinc-500/50 fe-panel-inset flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-zinc-600 flex-shrink-0" />
            <p className="text-sm text-zinc-300">
              <strong className="text-white">Hurry — Limited Seats.</strong>{" "}
              Netherlands intake: only 40 members per batch. Secure your
              interview slot today.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Why Choose Us ---------------- */
const WhyUs = () => (
  <section
    id="why"
    data-testid="why-section"
    className="py-20 md:py-28 fe-section-dark text-zinc-300"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="fe-divider-gold" />
        <div className="fe-overline text-zinc-400 mb-4">
          Why Choose EuroPath
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
          Genuine process.{" "}
          <span className="italic text-zinc-400">Real results.</span>
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-x-16 lg:gap-y-14">
        {WHY_US.map((w, i) => (
          <div
            key={w.title}
            data-testid={`why-${i}`}
            className="flex gap-5 md:gap-6"
          >
            <div className="flex-shrink-0">
              <div className="w-14 h-14 flex items-center justify-center bg-zinc-700 text-white">
                <w.icon className="w-6 h-6" />
              </div>
              <div className="fe-overline text-zinc-500 mt-3 text-center">
                0{i + 1}
              </div>
            </div>
            <div className="pt-1">
              <h3 className="font-display text-2xl font-bold text-white">
                {w.title}
              </h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">{w.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="py-20 md:py-28 fe-section-dark text-zinc-300"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5">
          <span className="fe-divider-gold" />
          <div className="fe-overline text-zinc-400 mb-4">Get in Touch</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight">
            Schedule your{" "}
            <span className="italic text-zinc-400">visit</span> to our
            Hyderabad office.
          </h2>
          <p className="mt-5 text-zinc-400 text-base md:text-lg">
            Confirm your available time and we'll set up a meeting with M. Sunil
            to review your profile and discuss the best European opportunity.
          </p>

          <div className="mt-10 space-y-1">
            <ContactRow
              icon={Phone}
              label="Phone"
              value={PHONE_DISPLAY}
              href={`tel:${PHONE}`}
              testId="contact-phone"
            />
            <ContactRow
              icon={MessageCircle}
              label="WhatsApp"
              value="Chat instantly"
              href={WHATSAPP_URL}
              external
              testId="contact-whatsapp"
              accent
            />
            <ContactRow
              icon={Mail}
              label="Email"
              value={EMAIL}
              href={`mailto:${EMAIL}`}
              testId="contact-email"
            />
            <ContactRow
              icon={MapPin}
              label="Office Address"
              value={OFFICE_ADDRESS_ONE_LINE}
              testId="contact-address"
            />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-btn"
              className="inline-flex items-center gap-2 px-6 h-12 bg-white hover:bg-zinc-200 text-black text-sm font-bold transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Message on WhatsApp
            </a>
            <a
              href={`tel:${PHONE}`}
              data-testid="contact-call-btn"
              className="inline-flex items-center gap-2 px-6 h-12 bg-white hover:bg-zinc-200 text-black text-sm font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative min-h-[420px] w-full border border-zinc-600/50 overflow-hidden sm:min-h-[480px] md:min-h-[560px]">
            <iframe
              title="EuroPath Immigration Services — Office Location"
              data-testid="office-map"
              src={GOOGLE_MAPS_EMBED_URL}
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
            <a
              href={GOOGLE_MAPS_PLACE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="open-in-maps"
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-700/90 backdrop-blur text-zinc-100 text-xs font-bold hover:bg-zinc-600 transition-colors shadow-lg border border-zinc-500/50"
            >
              Open in Google Maps
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <div className="absolute bottom-5 left-5 right-5 fe-card p-5 shadow-xl">
              <div className="flex items-start gap-3">
                <Globe2 className="w-5 h-5 text-zinc-600 mt-0.5" />
                <div>
                  <div className="fe-overline text-zinc-400">
                    Head Office · Hyderabad
                  </div>
                  <div className="font-display text-base font-bold text-white mt-1">
                    Venkateshwar Colony, Near Uppal Metro
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ContactRow = ({ icon: Icon, label, value, href, external, testId, accent }) => {
  const content = (
    <>
      <div
        className={`w-11 h-11 flex-shrink-0 flex items-center justify-center ${
          accent ? "bg-zinc-600 text-white" : "bg-zinc-600 text-white"
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="fe-overline text-zinc-500">{label}</div>
        <div className="text-[15px] font-semibold text-zinc-100 mt-0.5 break-words">
          {value}
        </div>
      </div>
      {href && (
        <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors flex-shrink-0" />
      )}
    </>
  );
  const className =
    "group flex items-center gap-4 py-4 border-b border-zinc-600/40 last:border-b-0 transition-colors";
  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={className}
      >
        {content}
      </a>
    );
  }
  return (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="fe-theme-footer pt-16 pb-8"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <BrandLogo />
          <p className="mt-6 text-sm text-zinc-700 max-w-md leading-relaxed">
            Genuine European job placements, work visas and end-to-end
            immigration support from our office in Hyderabad, India.
          </p>
          <p className="mt-5 font-display italic text-zinc-800 text-lg">
            “Your Path. Our Expertise, Your Future.”
          </p>
          <p className="mt-3 text-sm text-zinc-600">
            <a
              href={WEBSITE}
              target="_blank"
              rel="noreferrer"
              className="text-zinc-800 hover:text-black transition-colors"
            >
              www.europathimmigration.com
            </a>
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="fe-overline text-zinc-600 mb-4">Explore</div>
          <ul className="space-y-2.5 text-sm">
            {[
              ["#about", "About"],
              ["#countries", "Countries"],
              ["#sectors", "Job Sectors"],
              ["#benefits", "Salary & Benefits"],
              ["#why", "Why Choose Us"],
              ["#contact", "Contact"],
            ].map(([href, label]) => (
              <li key={href}>
                <a
                  href={href}
                  className="text-zinc-700 hover:text-black transition-colors"
                  data-testid={`footer-${label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="fe-overline text-zinc-600 mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-zinc-700">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-zinc-600" />
              <a
                href={`tel:${PHONE}`}
                className="text-zinc-800 hover:text-black"
                data-testid="footer-phone"
              >
                {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-zinc-600" />
              <a
                href={`mailto:${EMAIL}`}
                className="text-zinc-800 hover:text-black break-all"
                data-testid="footer-email"
              >
                {EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-zinc-600 flex-shrink-0" />
              <span>
                Plot No 8, Venkateshwar Colony, Pillar No C867, Near Uppal Metro
                Station, Uppal, Hyderabad – 500039
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 pt-6 border-t border-[#ddd4c0] flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-zinc-600">
          © {new Date().getFullYear()} EuroPath Immigration Services. All rights
          reserved.
        </p>
        <p className="text-xs text-zinc-600 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <a
            href={`${BASE}/admin/login.html`}
            className="text-zinc-700 hover:text-black transition-colors"
            data-testid="footer-staff-login"
          >
            Staff login
          </a>
          <span className="hidden sm:inline text-zinc-500">·</span>
          <span className="text-zinc-700">
            Hyderabad, India ·{" "}
            <a
              href={`mailto:${EMAIL}`}
              className="text-zinc-800 hover:text-black"
            >
              {EMAIL}
            </a>
          </span>
        </p>
      </div>
    </div>
  </footer>
);

/* ---------------- Sticky WhatsApp ---------------- */
const StickyWhatsapp = () => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noreferrer"
    data-testid="sticky-whatsapp"
    aria-label="Chat on WhatsApp"
    className="fe-pulse fixed bottom-20 right-5 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white flex items-center justify-center shadow-2xl transition-colors"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-7 h-7"
    >
      <path d="M20.52 3.48A11.9 11.9 0 0 0 12.06 0C5.5 0 .18 5.33.18 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.33-1.67a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.89-5.33 11.89-11.9 0-3.18-1.24-6.17-3.44-8.41zM12.07 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.76.99 1-3.66-.24-.38a9.86 9.86 0 0 1-1.51-5.26c0-5.46 4.45-9.9 9.93-9.9 2.65 0 5.14 1.03 7.01 2.9a9.83 9.83 0 0 1 2.9 7.01c0 5.46-4.45 9.89-9.92 9.89zm5.44-7.41c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.58-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.09 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z" />
    </svg>
  </a>
);

/* ---------------- Root ---------------- */
function App() {
  return (
    <div className="fe-app" data-testid="flyeuro-app">
      <Header />
      <main>
        <Hero />
        <JobMarquee />
        <About />
        <Countries />
        <Sectors />
        <Benefits />
        <WhyUs />
        <Contact />
      </main>
      <Footer />
      <StickyWhatsapp />
    </div>
  );
}

export default App;
